import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { AccordionItem } from 'foundations-components/transitional/components/AccordionItem';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import Raven from 'raven-js';
import { useCallback, useMemo, useState } from 'react';
import ActionErrorTag from '../action-formatters/ActionErrorTag';
import CardActionButtonList from '../action-formatters/CardActionButtonList';
import { ACTIONS_SELECTOR, TITLE_SELECTOR } from '../constants/CardSeleniumSelectors';
import { CARD_FORMATTER_IFRAME_LOOKUP_KEYS } from '../constants/IframeLookupKeys';
import { useCardContainerContext } from '../internal/CardContainerContext';
import { useDragDropComponentsContext } from '../internal/DragDropComponentsContext';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function CollapsibleListCardFormatter({
  actionComponent,
  actionConfigs,
  cardId,
  cardType,
  children,
  isCollapsed = false,
  hasActionsLoadingError,
  legacyDataAttributes: {
    ['data-sidebar-card-type']: legacyDataSidebarCardType,
    ['data-sidebar-card-association-object-type-id']: legacyDataSidebarCardAssociationObjectTypeId,
    ['data-sidebar-key']: legacyDataSidebarKey
  } = {},
  title,
  onCollapseCard,
  ActionFormatter = CardActionButtonList
}) {
  const dragDropComponents = useDragDropComponentsContext();
  const {
    reorderable
  } = useCardContainerContext();
  const [isHovered, setIsHovered] = useState(false);
  const DragHandle = useMemo(() => {
    if (reorderable && dragDropComponents != null && dragDropComponents.DragHandle != null) {
      return dragDropComponents.DragHandle;
    }
    if (reorderable) {
      Raven.captureMessage('CollapsibleListCardFormatter: no dragDropComponents passed in for location with reorderable=true, drag and drop functionality cannot be used');
    }
    return undefined;
  }, [dragDropComponents, reorderable]);
  const onOpenChange = useCallback(isOpen => {
    if (onCollapseCard) {
      onCollapseCard(cardId, !isOpen);
    }
  }, [cardId, onCollapseCard]);
  const toolbar = useMemo(() => {
    if (hasActionsLoadingError) {
      return /*#__PURE__*/_jsx(ActionErrorTag, {});
    }
    if (actionComponent) {
      return actionComponent;
    }
    if (actionConfigs) {
      return /*#__PURE__*/_jsx(ActionFormatter, {
        actionConfigs: actionConfigs
      });
    }
    return undefined;
  }, [hasActionsLoadingError, actionConfigs, actionComponent, ActionFormatter]);
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);
  let header = /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    justifyContent: "space-between",
    padding: "600",
    children: [/*#__PURE__*/_jsx(AccordionItem, {
      flush: true,
      isCardHeader: true,
      title: /*#__PURE__*/_jsx(TruncateString, {
        children: /*#__PURE__*/_jsx("span", {
          "data-selenium-test": TITLE_SELECTOR,
          children: title
        })
      }),
      open: !isCollapsed,
      onOpenChange: onOpenChange,
      size: "sm"
    }), toolbar && /*#__PURE__*/_jsx(View, {
      textAlign: "right",
      flexGrow: 1,
      flexShrink: 0,
      flexBasis: "auto",
      marginInlineStart: "300",
      children: /*#__PURE__*/_jsx("span", {
        "data-selenium-test": ACTIONS_SELECTOR,
        children: toolbar
      })
    })]
  });
  if (DragHandle) {
    header = /*#__PURE__*/_jsx(DragHandle, {
      style: isHovered ? undefined : {
        background: 'none'
      },
      children: header
    });
  }
  return /*#__PURE__*/_jsx(View, {
    as: "div",
    marginBlockEnd: "400",
    position: "relative",
    children: /*#__PURE__*/_jsxs(CardWrapper, {
      marginBlockEnd: "0",
      "data-sidebar-card-type": legacyDataSidebarCardType,
      "data-reorderable": reorderable,
      "data-sidebar-card-association-object-type-id": legacyDataSidebarCardAssociationObjectTypeId,
      "data-sidebar-key": legacyDataSidebarKey,
      "data-card-type": cardType,
      compact: true,
      expandable: true,
      expanded: !isCollapsed,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      children: [header, !isCollapsed && /*#__PURE__*/_jsx(CardSection, {
        paddingBlockStart: "0",
        children: children
      })]
    })
  });
}
CollapsibleListCardFormatter.iframeLookupKey = CARD_FORMATTER_IFRAME_LOOKUP_KEYS.COLLAPSIBLE_LIST_CARD_FORMATTER;